import * as React from "react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  EmptyState,
  LastUpdated,
  MultiSelect,
  PageHeader,
  Button,
  NavRail,
  Table,
  Icon,
  FailureReasonBadge,
  FAILURE_REASON_LABELS,
  type Column,
} from "@ardept/design-system";
import { useFailures, useMe, type FailuresFilter } from "../api/queries.js";
import { railVendorName } from "../auth/railVendorName.js";
import type { OperatorFailureDto } from "../api/types.js";
import "./FailuresScreen.css";
import "./screens.css";

/** Phase 19 D-FAIL-02: format ISO date string as relative timestamp "Ns ago", "Nm ago", etc.
 *  Inlined here (mirrors JobsScreen; formatRelativeTime is not exported from JobsScreen). */
function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const ms = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((now - ms) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = new Date(ms);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// Reason options derived from FAILURE_REASON_LABELS closed vocab
const REASON_OPTIONS = Object.keys(FAILURE_REASON_LABELS).map((r) => ({
  value: r,
  label: r,
}));

export function FailuresScreen(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: me } = useMe();

  // D-URL-01: parse filter from querystring — source of truth for filter state
  const filter: FailuresFilter = useMemo(
    () => ({
      tenant: params.getAll("tenant"),
      reason: params.getAll("reason"),
      since: params.get("since") ?? undefined,
      until: params.get("until") ?? undefined,
      after: params.get("after") ?? undefined,
    }),
    [params],
  );

  const { data, isLoading, error, dataUpdatedAt } = useFailures(filter);

  // Tenant options from /me allowed_tenants
  const tenantOptions = useMemo(
    () => (me?.allowed_tenants ?? []).map((t) => ({ value: t.slug, label: t.slug })),
    [me],
  );

  function setTenantFilter(vals: string[]) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("tenant");
      for (const t of vals) next.append("tenant", t);
      next.delete("after");
      return next;
    });
  }

  function setReasonFilter(vals: string[]) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("reason");
      for (const r of vals) next.append("reason", r);
      next.delete("after");
      return next;
    });
  }

  function setSince(val: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val) next.set("since", val);
      else next.delete("since");
      next.delete("after");
      return next;
    });
  }

  function setUntil(val: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val) next.set("until", val);
      else next.delete("until");
      next.delete("after");
      return next;
    });
  }

  function clearFilters() {
    setParams(new URLSearchParams());
  }

  function loadMore() {
    if (data?.next_cursor) {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("after", data.next_cursor!);
        return next;
      });
    }
  }

  const tenantFilter = filter.tenant ?? [];
  const reasonFilter = filter.reason ?? [];
  const sinceVal = filter.since ?? "";
  const untilVal = filter.until ?? "";

  const hasActiveFilters =
    tenantFilter.length > 0 || reasonFilter.length > 0 || !!sinceVal || !!untilVal;

  const columns: Column<OperatorFailureDto>[] = [
    {
      key: "tenant_slug",
      label: "Tenant",
      sortable: true,
      render: (r) => (
        <span className="cell-slug cell-tenant-chip">{r.tenant_slug}</span>
      ),
    },
    {
      key: "job_id",
      label: "Job",
      render: (r) => (
        <button
          className="cell-slug cell-job-btn"
          onClick={() => navigate(`/jobs/${r.id}`)}
          title={r.id}
          aria-label={`${r.id} open job detail`}
        >
          {r.id.slice(0, 8)}&hellip;
        </button>
      ),
    },
    {
      key: "workflow",
      label: "Workflow",
      sortable: true,
      render: (r) => <span className="cell-wf">{r.workflow_id}</span>,
    },
    {
      key: "failed_state",
      label: "Failed at state",
      render: (r) => <span className="cell-state cell-state--muted">{r.failed_state}</span>,
    },
    {
      key: "failure_reason",
      label: "Reason",
      sortable: true,
      render: (r) => <FailureReasonBadge reason={r.fail_reason} />,
    },
    {
      key: "failed_at",
      label: "Failed",
      sortable: true,
      sortValue: (r) => new Date(r.failed_at).getTime(),
      render: (r) => (
        <span className="cell-time" title={r.failed_at}>
          {formatRelativeTime(r.failed_at)}
        </span>
      ),
    },
  ];

  // Build operator display for NavRail from /me response
  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  // Filtered-empty state — EmptyState body takes string; use separate Clear Filters button below table
  const filteredEmptyState = (
    <div className="empty empty--failures-filtered">
      <span className="empty__ic">
        <Icon name="search-x" size={26} />
      </span>
      <h4>No failed jobs match these filters</h4>
      <p>
        <button className="btn btn--ghost" onClick={clearFilters}>
          Clear filters
        </button>
      </p>
    </div>
  );

  // Healthy-empty state
  const healthyEmptyState = (
    <div className="empty empty--healthy">
      <span className="empty__ic empty__ic--success">
        <Icon name="check-circle-2" size={26} />
      </span>
      <h4>No failed jobs in scope</h4>
      <p>Healthy is good.</p>
    </div>
  );

  const failures = data?.failures ?? [];

  const tableFooter =
    data && failures.length > 0 ? (
      <div className="tbl-foot__inner">
        <span className="count">
          Showing <b>{failures.length}</b> failed jobs
        </span>
        {data.next_cursor && (
          <Button variant="ghost" onClick={loadMore}>
            Load more
          </Button>
        )}
      </div>
    ) : null;

  return (
    <div className="app">
      <NavRail active="failures" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
      <main className="main">
        <PageHeader
          title="Failures"
          subtitle="Jobs that exited in the failed state. Read-only in v1.3. Retry and abandon ship in a later release."
        />
        {dataUpdatedAt && <LastUpdated timestamp={dataUpdatedAt} />}

        {/* Filter bar — mirrors JobsScreen layout */}
        <div className="filters">
          <div className="filters__group">
            <label>Tenant</label>
            <MultiSelect
              options={tenantOptions}
              value={tenantFilter}
              onChange={setTenantFilter}
              placeholder="All tenants"
            />
          </div>
          <div className="filters__group">
            <label>Reason</label>
            <MultiSelect
              options={REASON_OPTIONS}
              value={reasonFilter}
              onChange={setReasonFilter}
              placeholder="All reasons"
            />
          </div>
          <div className="filters__group">
            <label>Failed after</label>
            <input
              className="date-input"
              type="date"
              value={sinceVal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSince(e.target.value)}
            />
          </div>
          <div className="filters__group">
            <label>Failed before</label>
            <input
              className="date-input"
              type="date"
              value={untilVal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUntil(e.target.value)}
            />
          </div>
          <div className="filters__spacer" />
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters}>
              Reset filters
            </Button>
          )}
        </div>

        {/* Loading state */}
        {isLoading && !data && <div className="failures-loading">Loading failures...</div>}

        {/* Error state (inline banner — stays above last-known data if available) */}
        {error && (
          <div className="failures-error" role="status">
            Could not refresh failures (engine unreachable). Retrying every 4s.
          </div>
        )}

        {/* 403 empty state — no allowed tenants; using octagon-alert (ban not in IconName) */}
        {!isLoading && !error && me && me.allowed_tenants.length === 0 && (
          <EmptyState
            iconName="octagon-alert"
            title="No tenant access"
            body="Ask an administrator to grant you access to a tenant."
          />
        )}

        {/* Table or empty states */}
        {!isLoading && !error && (me?.allowed_tenants.length ?? 1) > 0 && (
          <Table
            columns={columns}
            rows={failures}
            initialSort={{ key: "failed_at", dir: "desc" }}
            emptyState={hasActiveFilters ? filteredEmptyState : healthyEmptyState}
            footer={tableFooter}
            caption="Failed jobs"
          />
        )}
      </main>
    </div>
  );
}

export default FailuresScreen;
